import React, { ChangeEvent } from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Button, Chip, createStyles, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid,
    Icon, IconButton, InputAdornment, makeStyles, Paper, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Theme, Tooltip, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';

import { CategoryModel, categoryModelInitial } from '../../../Models/CategoryModel';
import { SpotlightModel } from '../../../Models/SpotlightModel';
import SpotlightService from '../../../Services/SpotlightService';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import {InfluencerContext} from '../NewsContext';
import { ExtraParameterModel } from '../../../Models/ExtraParameterModel';
import { KeywordModel } from '../../../Models/KeywordModel';
import { ErrorStatus } from '../../../shared/enum/ErrorStatus';
import { ContentModel } from '../../../Models/ContentModel';
import { SourceModel } from '../../../Models/SourceModel';
import { ExtraParameterFieldType } from '../../../shared/enum/ExtraParameterFieldType';
import { OrganogramDto } from '../../../Models/OrganogramDto';
import { OrganogramsContext } from '../../../shared/context/OrganogramsContext';
import { Organogram } from '../../../shared/components/Organogram';
import ContentClientService from '../../../Services/ContentClientService';
import SourceService from '../../../Services/SourceService';
import ExtraParameterService from '../../../Services/ExtraParameterService';
import KeywordService from '../../../Services/KeywordService';
import OrganogramService from '../../../Services/OrganogramService';
import { EditAction } from '../../../shared/enum/EditAction';
import CategoryService from '../../../Services/CategoryService';
import { ClassificationStatus } from '../enum/ClassificationStatus';
import { NewsLoadingContext } from '../NewsLoadingContext';
import { LoadingStatus } from '../enum/LoadingStatus';
import { DetachedClassificationAction } from '../enum/DetachedClassificationAction';
import { HomeContext } from '../../Home/HomeContext';

const useStyles = makeStyles((theme: Theme) => createStyles({
    root: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    headtitle: {
        marginBottom: theme.spacing(3)
    },
    chipItem: {
        margin: '4px',
    },
    searchInput: {
        marginBottom: '4px',
    },
    keywordsComponents: {
        overflow: 'auto',
        maxHeight: '220px'
    },
    tableResume: {
        backgroundColor: theme.palette.background.default
    },
    titleSection: {
        marginBottom: theme.spacing(1)
    },
    itens: {
        width: '100%',
        color: 'primary',
    },
    text: {
        padding: theme.spacing(2),
    },
    loading: {
        minHeight: '20vh'
    },
    removedCategory: {
        color: theme.palette.text.disabled
    },
    tableCellHead: {
        color: theme.palette.primary.main,
    },
    tableCellRow: {
        backgroundColor: theme.palette.secondary.light,
        height: '4em',
    }
}));


export default function NewsCategorization() {

    const classes = useStyles();

    const newsContext = React.useContext(InfluencerContext);
    const homeContext = React.useContext(HomeContext);
    const newsLoadingContext = React.useContext(NewsLoadingContext);
    
    const [organogramDto, setOrganogramDto] = React.useState<OrganogramDto[]>([]);

    const [categoryCurrent, setCategoryCurrent] = React.useState<CategoryModel>(categoryModelInitial);

    const [contents, setContents] = React.useState<ContentModel[]>([]);
    const [spotlights, setSpotlights] = React.useState<SpotlightModel[]>([]);
    const [sources, setSources] = React.useState<SourceModel[]>([]);
    const [extraParameters, setExtraParameters] = React.useState<ExtraParameterModel[]>([]);
    const [extraParametersList, setExtraParametersList] = React.useState<ExtraParameterModel[]>([]);
    const [keywords, setKeywords] = React.useState<KeywordModel[]>([]);
    const [keywordsList, setKeywordsList] = React.useState<KeywordModel[]>([]);
    const [sourcesSelect, setSourcesSelect] = React.useState<SourceModel[]>([]);

    const [isCategorizationDisplayed, setIsCategorizationDisplayed] = React.useState(true);
    const [isConfirmDeletionOfCategorizationDialogOpen, setIsConfirmDeletionOfCategorizationDialogOpen] = React.useState(false);

    const [isNewCategorizationDialogOpen, setIsNewCategorizationDialogOpen] = React.useState(false);

    const [categoryToBeDeletedIndex, setCategoryToBeDeletedIndex] = React.useState(-1);

    const [contentNotDefinedError, setContentNotDefinedError] = React.useState(false);
    const [spotlightNotDefinedError, setSpotlightNotDefinedError] = React.useState(false);
    const [sourceNotDefinedError, setSourceNotDefinedError] = React.useState(false); 
    const [organogramError, setOrganogramError] = React.useState(false);
    const [extraParameterError, setExtraParameterError] = React.useState(false);
    const [keywordsError, setKeywordsError] = React.useState(false);

    const [searchKeyword, setSearchKeyword] = React.useState<string>('');
    const [copying, setCopying] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const [classificationEditMode, setClassificationEditMode] = React.useState(false);
    const [categoryEditMode, setCategoryEditMode] = React.useState(false);
    const [categoryEditIndex, setCategoryEditIndex] = React.useState(-1);

    React.useEffect(() => {
        if (newsContext.keepData)
            setIsCategorizationDisplayed(false);
    }, []);

    React.useEffect(() => {   

        if (newsContext.news.clientId < 1)
            return;

        ContentClientService.contentClientService(newsContext.news.clientId)
            .getAll()
            .then((res: AxiosResponse) => setContents(res.data))
            .catch((error: AxiosError) => homeContext.showError('', error));

        SourceService.sourceService(newsContext.news.clientId)
            .getAll()
            .then((res: AxiosResponse) =>setSources(res.data))
            .catch((error: AxiosError) => homeContext.showError('', error));

        ExtraParameterService.getExtraParameterByQuery(newsContext.news.clientId, false)
            .then((res: AxiosResponse) => {
                setExtraParameters(res.data);
                const parameters: ExtraParameterModel[] = JSON.parse(JSON.stringify(res.data));
                setExtraParametersList(parameters);
            })
            .catch((error: AxiosError) => homeContext.showError('', error));

        KeywordService.keywordService(newsContext.news.clientId)
            .getAll()
            .then((res: AxiosResponse) => {
                const data: KeywordModel[] = res.data;
                const noneElements = data.filter(x => x.description.toLowerCase() === 'nenhum');
                let otherElements = data.filter(x => x.description.toLowerCase() !== 'nenhum');
                otherElements = defineKeywordsOrdering(otherElements);

                if (noneElements.length > 0) {
                    otherElements = [...noneElements, ...otherElements];
                }
                setKeywords([...otherElements]);
                setKeywordsList([...otherElements]);                
            })
            .catch((error: AxiosError) => homeContext.showError('', error));

        OrganogramService.organogramService(newsContext.news.clientId)
            .getAll()
            .then((res: AxiosResponse) => {
                const organogramsDto: OrganogramDto[] = [{
                    level: 1, 
                    parentId: null, 
                    selectedOrganogram: null, 
                    hasOrganograms: false, 
                    organogramList: res.data
                }];                
                setOrganogramDto(organogramsDto);            
            })
            .catch((error: AxiosError) => homeContext.showError('', error));
                    
        SpotlightService.spotlightService()
            .getAll()
            .then((res: AxiosResponse) => setSpotlights(res.data))
            .catch((error: AxiosError) => homeContext.showError('', error));
    }, [newsContext.news.clientId]);

    React.useEffect(() => {
        const editMode = newsContext.classification.id > 0;
        setClassificationEditMode(editMode);
    }, [newsContext.classification.id]);

    React.useEffect(() => {
        if (newsLoadingContext.vehicleLoading == LoadingStatus.DONE && classificationEditMode)
            fetchEditCategories(newsContext.classification.id);
    }, [newsLoadingContext.vehicleLoading, classificationEditMode]);

    React.useEffect(() => {
        if (copying) {
            const categoryCopy: CategoryModel = JSON.parse(JSON.stringify(
                { ...newsContext.classification.categories[newsContext.classification.categories.length - 1] }));
            categoryCopy.copy = true;
            categoryCopy.editAction = EditAction.NONE;
            setCategoryCurrent(categoryCopy);     
            setOrganogramDto(categoryCopy.organograms);
            let listKeyword = [...keywordsList];
            const selectedKeyword = categoryCopy.keywordSelect ? [...categoryCopy.keywordSelect] : [];

            if (selectedKeyword && selectedKeyword.length > 0)
                listKeyword = listKeyword.filter(l => (selectedKeyword.findIndex(k => k.id == l.id) < 0) );

            setKeywords(listKeyword);
            setSourcesSelect([...categoryCopy.sources.filter(s => s.editAction != EditAction.DELETE)]);
            setExtraParameters((categoryCopy.extraParameter) ? [...categoryCopy.extraParameter] : []);
            setCopied(true);
            setCopying(false);
        } else {
            setCategoryCurrent({ ...categoryCurrent, copy: false });
        }
    }, [copying]);    

    React.useEffect(() => {
        if (newsContext.errorCategorizationStatus == ErrorStatus.RUN) {
            if (!newsContext.classification.categories ||
                (newsContext.classification.categories &&
                newsContext.classification.categories.filter(c => (c.editAction != EditAction.DELETE && c.active)).length == 0 )) {

                homeContext.showError('Ops.. você precisa inserir pelo menos uma categorização da notícia para poder salvar a classificação. ');

                newsContext.setErrorAtLeastOneCategorizationStatus(ErrorStatus.HAS_ERROR);
                newsContext.setErrorCategorizationStatus(ErrorStatus.HAS_ERROR);
            }
            else if (isCategorizationDisplayed) {
                setIsNewCategorizationDialogOpen(true);
                newsContext.setErrorCategorizationStatus(ErrorStatus.HAS_ERROR);
            } else {
                newsContext.setErrorCategorizationStatus(ErrorStatus.HAS_NOT_ERROR);
            }
        }
    }, [newsContext.errorCategorizationStatus]);

    React.useEffect(() => {
        if (newsContext.detachedNewsCategorizationAction === DetachedClassificationAction.CLEAR) {
            setCategoryCurrent(categoryModelInitial);
            newsContext.setDetachedNewsCategorizationAction(DetachedClassificationAction.NONE);
        }
    }, [newsContext.detachedNewsCategorizationAction]);

    const fetchEditCategories = (classificationId: number) => {
        newsLoadingContext.setCategorizationLoading(LoadingStatus.LOADING);
        CategoryService.categoryService(classificationId)
            .getAll()
            .then((res: AxiosResponse) => {
                if(res.data && res.data.length > 0) {
                    newsContext.setClassification({...newsContext.classification, categories: res.data});
                    setIsCategorizationDisplayed(false);
                }

                newsLoadingContext.setCategorizationLoading(LoadingStatus.DONE);
            })
            .catch((error: AxiosError) => {
                homeContext.showError('', error);
                newsLoadingContext.setCategorizationLoading(LoadingStatus.ERROR);
            });
    };
    
    const bringNoneElementsToFirstOrder = (keywords: KeywordModel[]) => {
        const noneElements = keywords.filter(x => x.description.toLowerCase() === 'nenhum');
        const otherElements = keywords.filter(x => x.description.toLowerCase() !== 'nenhum');
        return [ ...noneElements, ...otherElements ];
    };

    const sortKeywords = (keywords: KeywordModel[]) =>
        [...keywords].sort((a, b) => ('' + a.description).localeCompare(b.description));

    const defineKeywordsOrdering = (keywords: KeywordModel[]) => {
        keywords = sortKeywords(keywords);
        keywords = bringNoneElementsToFirstOrder(keywords);
        return keywords;
    };

    const handleSourceChange = (value: SourceModel[]) => {
        const array: SourceModel[] = [];

        value.forEach(source => {
            const found = categoryCurrent.sources?.find(c => c.id == source.id);
            if (found) { 
                if (found.editAction == EditAction.DELETE)
                    array.push({...found, editAction: EditAction.NONE});
                else
                    array.push({...found});
            }
            else
                array.push({...source, editAction: EditAction.INSERT});
        });

        categoryCurrent.sources?.forEach(source => {
            if (source) {
                const foundSource = array.find(c => c.id == source.id);           

                if (!foundSource && source.editAction != EditAction.INSERT)
                    array.push({...source, editAction: EditAction.DELETE});
            }
        }); 
        
        setSourcesSelect(array.filter(s => s.editAction != EditAction.DELETE));
        setCategoryCurrent({ ...categoryCurrent, sources: array });
    };    

    const handleAddCategory = () => {
        if (!hasError()) {
            const categoryModel: CategoryModel = { ...categoryCurrent };
            
            categoryModel.organograms = [...organogramDto];
            categoryModel.keywordNotSelect = keywords;
            categoryModel.extraParameter = extraParameters;

            if (categoryEditMode) {
                categoryModel.editAction = EditAction.UPDATE;

                const categories : CategoryModel[] =  JSON.parse(JSON.stringify(newsContext.classification.categories));

                categories[categoryEditIndex] = JSON.parse(JSON.stringify(categoryModel));

                newsContext.setClassification({ ...newsContext.classification, categories: categories });

            } else {
                categoryModel.editAction = EditAction.INSERT;

                if (newsContext.classification.categories)
                    newsContext.setClassification({ ...newsContext.classification, categories: [...newsContext.classification.categories, categoryModel] });
                else
                    newsContext.setClassification({ ...newsContext.classification, categories: [categoryModel] });
            }
            handleCloseCategory();
        }
    };

    const handleEditCategory = (category: CategoryModel, index: number) => {
        setCategoryCurrent(category); 
        setOrganogramDto(JSON.parse(JSON.stringify(category.organograms)));
                
        let listKeyword = [...keywordsList];
        const selectedKeyword = category.keywordSelect ? [...category.keywordSelect] : [];

        if (selectedKeyword && selectedKeyword.length > 0)
            listKeyword = listKeyword.filter(l => (selectedKeyword.findIndex(k => k.id == l.id) < 0) );

        setKeywords(listKeyword);
        setExtraParameters(category.extraParameter ? [...category.extraParameter] : []);
        setSourcesSelect(category.sources ? [...category.sources.filter(s => s.editAction != EditAction.DELETE)] : []);
            
        setIsCategorizationDisplayed(true);
        setCategoryEditIndex(index); 
        setCategoryEditMode(true);
    };

    const handleDeleteCategory = (index: number): void => {
        const categoriesLocal = [...newsContext.classification.categories];

        if (classificationEditMode) {

            categoriesLocal[index].active = false;

            if (categoriesLocal[index].editAction == EditAction.RESTORE)
                categoriesLocal[index].editAction = EditAction.NONE;
            else if (categoriesLocal[index].editAction == EditAction.INSERT)
                categoriesLocal.splice(index, 1);
            else
                categoriesLocal[index].editAction = EditAction.DELETE;
        }
        else
            categoriesLocal.splice(index, 1);
            
        newsContext.setClassification({ ...newsContext.classification, categories: categoriesLocal });
        setIsConfirmDeletionOfCategorizationDialogOpen(false);

        handleCloseCategory();
    };

    const handleCloseCategory = () => {
        setIsCategorizationDisplayed(false);
        setSearchKeyword('');

        setCategoryCurrent(categoryModelInitial);            
        const organogramsDto: OrganogramDto[] = [{
            level: 1, 
            parentId: null, 
            selectedOrganogram: null, 
            hasOrganograms: false, 
            organogramList: organogramDto[0].organogramList
        }];   
        setOrganogramDto(organogramsDto);

        setKeywords([...keywordsList]);
        setExtraParameters(JSON.parse(JSON.stringify(extraParametersList)));
        setSourcesSelect([]);
        
        setCopied(false);
        setCategoryEditIndex(-1);
        setCategoryEditMode(false);
    };

    const handleCopyDataFromPreviousCategorization = () => {
        setExtraParameters([]);
        setSourcesSelect([]);
        setKeywords([]);        
        setOrganogramDto([]);
        setCopying(true);
    };

    const handleRestoreCategory = (index: number) => {
        if (classificationEditMode) {
            const categoriesLocal = [...newsContext.classification.categories];
            categoriesLocal[index].active = true;

            if (categoriesLocal[index].editAction == EditAction.NONE)
                categoriesLocal[index].editAction = EditAction.RESTORE;
            else if (categoriesLocal[index].editAction == EditAction.DELETE)
                categoriesLocal[index].editAction = EditAction.NONE;

            newsContext.setClassification({ ...newsContext.classification, categories: categoriesLocal });
        }
    };

    const handleExtraParameterChange = (parameter: ExtraParameterModel, values: string | string[] | null) => {
        const index = extraParameters.findIndex(p => p.id == parameter.id);
        const currentParameter = JSON.parse(JSON.stringify(extraParameters[index]));

        if (Array.isArray(values)) {
            if (values.length > 0)
                currentParameter.value = values.join(';');   
            else
                currentParameter.value = '';
        }
        else {
            currentParameter.value = values;
        }

        if (classificationEditMode && categoryEditMode)
            currentParameter.editAction = EditAction.UPDATE;
        else
            currentParameter.editAction = EditAction.INSERT;

        const listParameters = JSON.parse(JSON.stringify(extraParameters));
        listParameters[index] = currentParameter;

        setExtraParameters(listParameters);
    };

    const hasError = () => {
        const errorContent: boolean = contents && contents.length > 0 && !categoryCurrent.content;
        const errorSpotlight: boolean = spotlights && spotlights.length > 0 && !categoryCurrent.spotlight;
        const errorSource: boolean = (sources && sources.length > 0 &&
            !(categoryCurrent.sources && categoryCurrent.sources.filter(s => s.editAction != EditAction.DELETE).length > 0));
        const errorKeyword: boolean = (keywords && keywords.length > 0 &&
            !(categoryCurrent.keywordSelect && categoryCurrent.keywordSelect.filter(k => k.editAction != EditAction.DELETE).length > 0));

        setContentNotDefinedError(errorContent);
        setSpotlightNotDefinedError(errorSpotlight);
        setSourceNotDefinedError(errorSource);
        setKeywordsError(errorKeyword);        

        const errorOrganogram = organogramDto.filter((e: OrganogramDto) =>
            (e.selectedOrganogram == null) && (e.organogramList.length > 0)).length > 0;   
        setOrganogramError(errorOrganogram);

        let errorExtraParameters = false;
        extraParameters.forEach((extraParameter) => {
            if (!extraParameter.value || extraParameter.value == '') {
                errorExtraParameters = true;
            }
        });
        setExtraParameterError(errorExtraParameters);

        const error = (errorContent || errorSpotlight || errorSource || errorExtraParameters || errorKeyword || errorOrganogram);

        if (error)
            homeContext.showError('Ops.. não foi possível adicionar categoria. Para adicionar uma nova categoria é necessário preencher todos os campos obrigatórios.');

        return error;
    };

    const getResumeTableIcons = (category: CategoryModel, index: number) => {
        if (category.active) {
            return (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => handleEditCategory(JSON.parse(JSON.stringify(category)), index)}
                            disabled={newsContext.classification.status == ClassificationStatus.REMOVED}>
                            <Icon>edit</Icon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                        <IconButton
                            size="small"
                            onClick={() => {
                                setCategoryToBeDeletedIndex(index);
                                setIsConfirmDeletionOfCategorizationDialogOpen(true);
                            }}
                            disabled={newsContext.classification.status == ClassificationStatus.REMOVED}>
                            <Icon>delete_outline</Icon>
                        </IconButton>
                    </Tooltip>
                </>
            );
        }
        else {
            return (
                <Tooltip title="Restaurar">
                    <IconButton
                        size="small"
                        onClick={() => handleRestoreCategory(index)}
                        disabled={newsContext.classification.status == ClassificationStatus.REMOVED}>
                        <Icon>restore_page</Icon>
                    </IconButton>
                </Tooltip>
            );
        }
    };

    const getResume = () => {
        return (
            <>
                {newsContext.classification.categories && newsContext.classification.categories.length > 0 &&
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.headtitle}>Resumo das categorias adicionadas</Typography>
                        <TableContainer component={Paper} elevation={0} className={classes.tableResume}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow  className={classes.tableCellRow}>
                                        <TableCell className={classes.tableCellHead} align="center">Marcas</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Área/departamento</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Tema</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Subtema 1</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Subtema 2</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Teor</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Destaque</TableCell>
                                        <TableCell className={classes.tableCellHead} align="center">Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {newsContext.classification.categories.map((category, index) => (
                                        <TableRow key={index}>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.organograms[0]
                                                    ? (category.organograms[0].selectedOrganogram
                                                        ? category.organograms[0].selectedOrganogram?.description
                                                        : '-')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.organograms[1]
                                                    ? (category.organograms[1].selectedOrganogram
                                                        ? category.organograms[1].selectedOrganogram?.description
                                                        : '-')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.organograms[2]
                                                    ? (category.organograms[2].selectedOrganogram
                                                        ? category.organograms[2].selectedOrganogram?.description
                                                        : '-')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.organograms[3]
                                                    ? (category.organograms[3].selectedOrganogram
                                                        ? category.organograms[3].selectedOrganogram?.description
                                                        : '-')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.organograms[4]
                                                    ? (category.organograms[4].selectedOrganogram
                                                        ? category.organograms[4].selectedOrganogram?.description
                                                        : '-')
                                                    : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.content ? category.content.name : '-'}
                                            </TableCell>
                                            <TableCell align="center" className={clsx({[classes.removedCategory]: !category.active})}>
                                                {category.spotlight ? category.spotlight.name : '-'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {getResumeTableIcons(category, index)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                }
            </>
        );
    };    

    const getOrganogram = () => {
        return (         
            <>   
                <OrganogramsContext.Provider value={{ 
                    organogramDto, setOrganogramDto 
                }}>                        
                    {organogramDto.map((item: OrganogramDto) =>
                        <Grid item xs={12} sm={6} key={`grid-organogram-${item.parentId}-${item.selectedOrganogram ? item.selectedOrganogram.id : 0}`}>
                            <Organogram 
                                key={`organogram-${item.parentId}-${item.selectedOrganogram ? item.selectedOrganogram.id : 0}`} 
                                level={item.level} 
                                error={organogramError} 
                                parentId={item.parentId} 
                                selected={item.selectedOrganogram} 
                                organogramsList={item.organogramList} />
                        </Grid>                   
                    )}
                </OrganogramsContext.Provider>                
            </>
        );
    };

    const getContents = () => {
        return (
            <>
                {contents && contents.length > 0 &&
                    <Grid item xs={12}>
                        <FormControl component="fieldset" error={contentNotDefinedError}>
                            <FormLabel component="legend" error={false}>
                                <Typography variant="subtitle1">Teor</Typography>
                            </FormLabel>
                            <RadioGroup
                                row
                                value={categoryCurrent.content ? categoryCurrent.content.id.toString() : ''}
                                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                    setCategoryCurrent({ ...categoryCurrent,
                                        content: (contents.find(e => e.id == Number(event.target.value)) || null) })}>
                                <Grid container spacing={3}>
                                    {contents.map(content =>
                                        <Grid item key={content.id}>
                                            <FormControlLabel
                                                value={content.id.toString()}
                                                control={<Radio color='primary' />}
                                                label={content.name} />
                                        </Grid>
                                    )}
                                </Grid>
                            </RadioGroup>
                            {contentNotDefinedError && !categoryCurrent.content &&
                                <FormHelperText>Selecione um teor</FormHelperText>}
                        </FormControl>
                    </Grid>
                }
            </>
        );
    };

    const getSpotlights = () => {
        return (
            <Grid item xs={12}>
                <FormControl component="fieldset" error={spotlightNotDefinedError}>
                    <FormLabel component="legend" error={false}>
                        <Typography variant="subtitle1">Destaque</Typography>
                    </FormLabel>
                    <RadioGroup
                        row
                        value={categoryCurrent.spotlight ? categoryCurrent.spotlight.id.toString() : ''}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setCategoryCurrent({ ...categoryCurrent,
                                spotlight: spotlights.find(e => e.id == Number(event.target.value)) || null }); }}>
                        <Grid container spacing={3}>
                            {spotlights.map(spotlight =>
                                <Grid item key={spotlight.id}>
                                    <FormControlLabel 
                                        value={spotlight.id.toString()} 
                                        control={<Radio color='primary'/>} 
                                        label={spotlight.name} />
                                </Grid>
                            )}
                        </Grid>
                    </RadioGroup>
                    {spotlightNotDefinedError && !categoryCurrent.spotlight &&
                        <FormHelperText>Selecione um destaque</FormHelperText>}
                </FormControl>
            </Grid>
        );
    };

    const getEndAdormentKeyword = () => {
        if (searchKeyword.length > 0) {
            return (
                <InputAdornment position="end">
                    <IconButton onClick={() => setSearchKeyword('')}>
                        <Icon className={'material-icons-round'}>close</Icon>
                    </IconButton>
                </InputAdornment>
            );
        }
    };

    const getKeywords = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" className={classes.titleSection}>Palavras-chave/tags</Typography>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id='search-key-word'
                            value={searchKeyword}
                            label='Pesquisar'
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setSearchKeyword(event.target.value); }}
                            className={classes.searchInput}
                            fullWidth
                            InputProps={{
                                startAdornment: <InputAdornment position="start">
                                    <Icon className={'material-icons-round'}>search</Icon>
                                </InputAdornment>,
                                endAdornment: getEndAdormentKeyword()
                            }}
                            error={keywordsError &&
                                !(categoryCurrent.keywordSelect && categoryCurrent.keywordSelect.filter(k => k.editAction != EditAction.DELETE).length > 0)}
                            helperText={keywordsError &&
                                !(categoryCurrent.keywordSelect && categoryCurrent.keywordSelect.filter(k => k.editAction != EditAction.DELETE).length > 0)
                                ? 'Selecione pelo menos uma palavra-chave'
                                : ''}
                        />
                    </Grid>
                    <Grid item xs={12} className={classes.keywordsComponents}>
                        {categoryCurrent.keywordSelect && renderKeywordSelect()}
                        {keywords && renderKeywordNotSelect()}
                    </Grid>
                </Grid>
            </>
        );
    };

    const renderKeywordSelect = () => {
        return (
            <Grid item xs={12}>
                {categoryCurrent?.keywordSelect &&
                    categoryCurrent?.keywordSelect.filter(k => k.editAction != EditAction.DELETE).map((keyword) => {
                        return (
                            <Chip
                                key={keyword.id}
                                label={keyword.description}
                                className={classes.chipItem}
                                deleteIcon={<Icon>close</Icon>}
                                onDelete={() => { handleSelectToList(keyword); }}
                                onClick={() => { handleSelectToList(keyword); }}
                                color='primary'
                            />
                        );
                    })}
            </Grid>
        );
    };

    const handleSelectToList = (keyword: KeywordModel) => {
        let listKeywordSelect = (categoryCurrent.keywordSelect) ?
            [...categoryCurrent.keywordSelect] : [];
        listKeywordSelect = defineKeywordsOrdering(listKeywordSelect);
        const index = listKeywordSelect.findIndex(k => k.id == keyword.id);

        if (keyword.editAction != EditAction.INSERT)
            listKeywordSelect[index].editAction = EditAction.DELETE;
        else
            listKeywordSelect.splice(index, 1);

        let listKeywordNotSelect = keywords;
            
        listKeywordNotSelect.push(keyword);
        listKeywordNotSelect = defineKeywordsOrdering(listKeywordNotSelect);

        setCategoryCurrent({
            ...categoryCurrent,
            keywordSelect: listKeywordSelect,
        });
        setKeywords(listKeywordNotSelect);
    };

    const renderKeywordNotSelect = () => {
        const listFilter = (searchKeyword != '') ?
            keywords.reduce(function (filtered: KeywordModel[], keyword) {
                if (keyword.description.toLowerCase().includes(searchKeyword.toLowerCase()))
                    filtered.push(keyword);

                return filtered;
            }, []) : keywords;

        return (
            <Grid item xs={12}>
                {listFilter && listFilter.map((keyword) => {
                    return <Chip
                        key={keyword.id}
                        label={keyword.description}
                        className={classes.chipItem}
                        onClick={() => {
                            const listKeywordSelect = (categoryCurrent.keywordSelect) ?
                                [...categoryCurrent.keywordSelect] : [];
                            
                            if (keyword.editAction != EditAction.DELETE) {
                                keyword.editAction = EditAction.INSERT;
                                listKeywordSelect.push(keyword);
                            }
                            else {
                                const index = listKeywordSelect.findIndex(k => k.id == keyword.id);
                                listKeywordSelect[index].editAction = EditAction.NONE;
                            }

                            const listKeywordNotSelect = keywords;
                            const index = listKeywordNotSelect.findIndex(k => k.id == keyword.id);
                            listKeywordNotSelect.splice(index, 1);

                            setCategoryCurrent({
                                ...categoryCurrent,
                                keywordSelect: listKeywordSelect
                            });
                            setKeywords(listKeywordNotSelect);
                        }}
                        color='primary'
                        variant="outlined"
                    />;
                })}
            </Grid>
        );
    };

    const getSources = () => {
        return (
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    multiple
                    id="sources"
                    options={sources}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    filterSelectedOptions
                    value={sourcesSelect}
                    onChange={(_, value: SourceModel[]) => { handleSourceChange(value); }}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    className={classes.itens}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Fontes"
                            error={sourceNotDefinedError && !(categoryCurrent.sources && categoryCurrent.sources.filter(s => s.editAction != EditAction.DELETE).length > 0)}
                            helperText={sourceNotDefinedError && !(categoryCurrent.sources && categoryCurrent.sources.filter(s => s.editAction != EditAction.DELETE).length > 0)
                                ? 'Selecione pelo menos uma fonte'
                                : ''} />
                    )} />
            </Grid>
        );
    };

    const getExtraParameters = () => {
        return (
            <>
                {extraParameters && extraParameters.length > 0 && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1">Análise de mensagem chave</Typography>
                        </Grid>
                        {extraParameters.sort((a,b) => a.id - b.id).map((parameter: ExtraParameterModel) => {
                            if (parameter.type == ExtraParameterFieldType.TEXT) {
                                return (
                                    <Grid item 
                                        xs={12} 
                                        sm={parameter.description ? (parameter.description.length > 40 ? 12 : 6) : 6}
                                        md={parameter.description ? (parameter.description.length > 60 ? 12 : 6) : 6}>
                                        <TextField
                                            key={parameter.id}
                                            id={parameter.description}
                                            value={parameter.value ? parameter.value : ''}
                                            label={parameter.description}
                                            onChange={(event) => handleExtraParameterChange(parameter, event.target.value)}
                                            variant="outlined"
                                            fullWidth
                                            error={extraParameterError && (!parameter.value  || parameter.value == '')}
                                            helperText={extraParameterError && (!parameter.value  || parameter.value == '')
                                                ? 'Campo obrigatório'
                                                : ''} />
                                    </Grid>
                                );
                            } else {
                                const multiple = parameter.type == ExtraParameterFieldType.SELECT_MULTIPLE;

                                return (
                                    <Grid item 
                                        xs={12} 
                                        sm={parameter.description ? (parameter.description.length > 40 ? 12 : 6) : 6}
                                        md={parameter.description ? (parameter.description.length > 60 ? 12 : 6) : 6}>
                                        <Autocomplete
                                            multiple={multiple}
                                            key={parameter.id}
                                            id={parameter.description}
                                            value={parameter.value
                                                ? (multiple ? parameter.value.split(';') : parameter.value)
                                                : (multiple ? [] : null)}
                                            onChange={(_, value: string | string[] | null) => handleExtraParameterChange(parameter, value)}
                                            options={parameter.answers ? parameter.answers.split(';') : []}
                                            getOptionLabel={(option: string) => option}
                                            filterSelectedOptions={multiple}
                                            noOptionsText="Sem resultados"
                                            loadingText="Carregando..."
                                            renderTags={(value, getTagProps) =>
                                                value.map((option, index) => (
                                                    <Chip
                                                        key={index}
                                                        variant="outlined"
                                                        label={option}
                                                        {...getTagProps({ index })} />
                                                ))
                                            }
                                            renderInput={(params) =>
                                                <TextField {...params}
                                                    label={parameter.description}
                                                    variant="outlined"
                                                    error={extraParameterError && (!parameter.value  || parameter.value == '')}
                                                    helperText={extraParameterError && (!parameter.value  || parameter.value == '')
                                                        ? 'Campo obrigatório'
                                                        : ''} />
                                            } />
                                    </Grid>
                                );
                            }
                        })}
                    </>
                )}
            </>
        );
    };

    const getCategorizationButtons = () => {
        return (
            <Grid item xs={12}>
                <Grid 
                    container
                    direction="row"
                    justify="flex-end"
                    alignItems="flex-end"
                    spacing={2}>
                    { categoryEditMode && 
                    <Grid item>
                        <Button
                            onClick={handleCloseCategory}
                            size="large"
                            variant="outlined"
                        >
                            DESCARTAR
                        </Button>
                    </Grid>
                    }
                    <Grid item>
                        <Button
                            onClick={handleAddCategory}
                            size="large"
                            variant="outlined"
                            color="primary" 
                        >
                            { categoryEditMode ? 'Salvar' : 'Adicionar Categoria'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    };

    const getNewCategorization = () =>
        <>
            {newsContext.classification.categories && newsContext.classification.categories.length > 0 && !copied && !categoryEditMode &&
                <Grid container xs={12} justify="flex-start" >
                    <Button
                        onClick={handleCopyDataFromPreviousCategorization}
                        variant='text'
                        startIcon={<Icon color="primary">file_copy_outlined</Icon>}>
                        Copiar dados da categorização anterior
                    </Button>
                </Grid>
            }
            {newsContext.classification.categories && newsContext.classification.categories.length > 0 && copied &&
                <Grid
                    spacing={1}
                    container
                    xs={12}
                    justify="flex-start"
                    className={classes.text} >
                    <Typography variant="body1">
                        Dados copiados com sucesso!
                    </Typography>
                </Grid>
            }

            { getOrganogram() }            
            { getContents() }
            { getSpotlights() }
            { getKeywords() }
            { getSources() }
            { getExtraParameters() }

            { getCategorizationButtons() }
        </>;

    return (
        <>
            <Grid container spacing={3}>
                {getResume()}
                {isCategorizationDisplayed || !newsContext.classification.categories.length
                    ? getNewCategorization()
                    : (newsContext.classification.status != ClassificationStatus.REMOVED && (
                        <Grid container xs={12} justify="flex-start" >
                            <Button
                                variant='text'
                                onClick={() => setIsCategorizationDisplayed(true)}
                                startIcon={<Icon color="primary">add</Icon>}>
                                    Adicionar Nova Categorização
                            </Button>
                        </Grid>
                    ))
                }
            </Grid>

            <BasicConfirmDialog
                title="Tem certeza de que deseja excluir essa categorização?"
                description="Ao excluir, você perderá todos os dados dessa categorização."
                handleFirstButton={() => handleDeleteCategory(categoryToBeDeletedIndex)}
                handleSecondButton={() => setIsConfirmDeletionOfCategorizationDialogOpen(false)}
                textFirstButton="Excluir"
                textSecondButton="Cancelar"
                open={isConfirmDeletionOfCategorizationDialogOpen} />
            <BasicConfirmDialog
                title="Salvar sem nova categorização?"
                description="Você preencheu uma nova categorização para esta notícia, porém ela não foi adicionada à classificação."
                handleFirstButton={() => { setIsCategorizationDisplayed(false); setIsNewCategorizationDialogOpen(false); }}
                handleSecondButton={() => setIsNewCategorizationDialogOpen(false)}
                textFirstButton="Salvar sem nova categorização"
                textSecondButton="Voltar"
                open={isNewCategorizationDialogOpen} />
        </>
    );
}